/* Who has just arrived in a survival game, off the payload the server sent (SV11/SV12).

   The owner asked for the map to "zoom to the specific bot that appeared, one by one, so players
   are aware", and this is the list it walks. Pure and shared, because three things ask the same
   question and must agree: the tour itself, the round digest (which waits for the tour rather
   than opening over it), and the test that pins the order.

   Two rules it keeps and both are about the same accident. An arrival is identified by
   `"<gameId>:<tick>:<userId>"` — not by the bot alone, since a game spawns one account once but a
   payload is reduced many times, and not without the game either, since nothing clears this list
   between two games played in one session and the cast of bots is fixed — and an arrival already
   in `toured` is not an arrival. `game.metadata.events` is the round's own list, cleared at the
   top of every round, so there is nothing to expire. */

// Wave 0's events ride in the payload a player loads the game with, so opening a survival game
// tours the board it opens on; later waves arrive in their round's broadcast.
export const spawnArrivalKey = (gameId, tick, userId) => `${gameId ?? 0}:${tick ?? 0}:${userId}`

export const spawnArrivals = (game, toured = []) => {
  const seen = new Set(toured)
  const players = game?.players || []
  return (game?.metadata?.events || [])
    .filter((event) => event.type === "enemy_appeared" && Array.isArray(event.coordinates))
    .map((event) => {
      const userId = Number(event.detail?.user_id)
      const seat = players.find((player) => Number(player?.user?.id) === userId) || null
      return {
        key: spawnArrivalKey(game?.id, game?.tick, userId),
        userId,
        // The bot's first crown: where the event points, where the digest's door pings, and where
        // the camera goes.
        column: Number(event.coordinates[0]),
        row: Number(event.coordinates[1]),
        wave: Number(event.detail?.wave ?? 0),
        // Read off the seat rather than off the event: the wire carries ids, and what a player
        // reads is the roster's own words for them.
        name: seat?.user?.username ?? null,
        race: seat?.race ?? null,
        difficulty: seat?.botDifficulty ?? null,
        // And who it came for (29/F4): the banner says it out loud, because in a two-player
        // survival game "whose is this one?" is the first thing either of them asks.
        target:
          players.find(
            (player) => Number(player?.user?.id) === Number(event.detail?.target_user_id)
          )?.user?.username ?? null,
        // A newcomer whose seat has not landed in `players` yet is not toured: the banner would
        // name nobody. It keeps its key out of `toured`, so the next payload — the one carrying
        // the row — tours it.
        seated: Boolean(seat),
      }
    })
    .filter((arrival) => arrival.seated && Number.isFinite(arrival.userId) && !seen.has(arrival.key))
}
